import {Divider} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import Image from 'next/image';
import {IoMdPersonAdd} from 'react-icons/io';
import {
  MdEmail,
  MdExitToApp,
  MdForum,
  MdInsertDriveFile,
  MdOutlineDomain,
} from 'react-icons/md';
import {LOGO_IMAGE_PATH_FULL} from '../../../../config/constants/img';
import {PROFILE_AC_EMAIL} from '../../../../config/constants/routes';
import {isReleasedRedesignTop} from '../../../../config/unReleasePage';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {getMailAuthLinkWithParamsHooks} from '../../../../shared/services/getMailAuthLinkWithParamsHooks';

type Props = {
  isStudentVerificationRequired?: boolean;
};

export const LogInGuideHeaderForPc: React.FC<Props> = ({
  isStudentVerificationRequired,
}) => {
  const mailAuthLink = getMailAuthLinkWithParamsHooks({});

  const promotionList: Array<{
    title: string;
    icon: React.ReactNode;
    description: string;
  }> = [
    {
      title: '厳選企業の新卒採用情報',
      icon: <MdOutlineDomain size={36} />,
      description:
        '外資/日系各業界トップ企業のインターン・本選考情報を毎日更新',
    },
    {
      title: '先輩就活生の体験記',
      icon: <MdInsertDriveFile size={36} />,
      description: '難関選考を突破した先輩のリアルレポートで詳細な対策を',
    },
    {
      title: 'Premiumスカウト',
      icon: <MdEmail size={36} />,
      description:
        '限定ルートや特別イベントへの招待メッセージが「あなた宛」に届く',
    },
    {
      title: '選考対策コラム',
      icon: <MdForum size={36} />,
      description: '企業解説・インタビュー・ノウハウなど最新情報を発信',
    },
    {
      title: 'コミュニティ',
      icon: <MdForum size={36} />,
      description: '内定者や就活生同士で業界研究・選考対策などを語り合おう',
    },
  ];

  if (isReleasedRedesignTop()) {
    return (
      <div className="hidden pc:flex flex-col w-full bg-white rounded-16 p-16 gap-16 my-16">
        <div className="text-base text-black-1">
          外資就活ドットコムは、プロフェッショナルを目指す学生のための就職活動支援メディアです。
          {isStudentVerificationRequired &&
            '会員限定サービスのご利用には大学認証の完了が必要です。'}
        </div>
        <div className="w-full">
          <Divider
            size={2}
            label={
              <div className="border-[1px] p-8 px-16 border-black-1 text-black-1 rounded-full">
                会員限定サービス
              </div>
            }
            labelPosition="center"
          />
        </div>
        <div className="w-full flex gap-16">
          {promotionList.map((promotion) => (
            <div
              key={promotion.title}
              className="flex flex-col flex-1 bg-background-1 rounded-16 p-16 gap-8 text-black-1"
            >
              <div>{promotion.icon}</div>
              <div className="font-bold">{promotion.title}</div>
              <div>{promotion.description}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-8 items-center justify-center mx-auto my-8">
          {isStudentVerificationRequired ? (
            <CommonLink
              href={PROFILE_AC_EMAIL}
              className="bg-primary-1 text-white font-bold border-2 border-primary-1 py-8 px-24 rounded-full"
            >
              <div className="flex justify-center items-center">
                大学認証を完了する
              </div>
            </CommonLink>
          ) : (
            <>
              <CommonLink
                href={mailAuthLink}
                className="bg-accent-1 text-white font-bold border-2 border-accent-1 py-8 px-24 rounded-full"
              >
                <div className="flex justify-center items-center">
                  <IoMdPersonAdd size={16} className="mr-4" />
                  新規会員登録
                </div>
              </CommonLink>
              <CommonLink
                href={'/login'}
                className="flex text-primary font-bold border-2 border-primary py-8 px-20 rounded-full"
              >
                <div className="flex justify-center items-center">
                  <MdExitToApp size={16} className="mr-4" />
                  ログイン
                </div>
              </CommonLink>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="hidden pc:block w-[1200px] h-[413px] bg-white p-[32px] pc:mt-12">
      <div className="flex items-end gap-16">
        <Image
          src={LOGO_IMAGE_PATH_FULL}
          alt="外資就活ロゴ"
          width={150}
          height={33}
        />

        <div className="text-base">
          外資就活ドットコムは、プロフェッショナルを目指す学生のための就職活動支援メディアです。
        </div>
      </div>
      <div className="relative flex items-center my-24">
        <div className="flex-grow border-t border-line" />
        <span className="flex-shrink mx-24 text-xl font-semibold">
          会員限定サービス
        </span>
        <div className="flex-grow border-t border-line" />
      </div>
      <div className="flex justify-center">
        <div className="w-[253px] h-[167px] text-center">
          <MdOutlineDomain size="87" className="mx-auto" />
          <div className="font-bold text-lg">厳選企業の募集情報</div>
          <div className="text-black-1">
            <div>外資系・日系トップ企業の最新</div>
            <div>インターン・本選考情報が届く</div>
          </div>
        </div>
        <div className="w-[253px] h-[167px] text-center">
          <MdInsertDriveFile size={87} className="mx-auto" />
          <div className="font-bold text-lg">先輩のES・体験記</div>
          <div className="text-black-1">
            <div>実際のインターンや本選考に</div>
            <div>関する体験レポートが読める</div>
          </div>
        </div>
        <div className="w-[253px] h-[167px] text-center">
          <MdForum size={87} className="mx-auto" />
          <div className="font-bold text-lg">内定者に聞くコミュニティ</div>
          <div className="text-black-1">
            <div>トップ企業内定者や社会人OBに</div>
            <div>就活の疑問・悩みを聞ける</div>
          </div>
        </div>
        <div className="w-[253px] h-[167px] text-center">
          <MdEmail size={87} className="mx-auto" />
          <div className="font-bold text-lg">Premiumスカウト</div>
          <div className="text-black-1">
            <div>厳選した優良企業から、</div>
            <div>会食や面接などのスカウトが届く</div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center">
        <div className="w-[50%]">
          <BasicButton
            className="mt-[12px] w-[50%] block"
            children={
              isStudentVerificationRequired ? (
                <span className="flex justify-center">大学認証を完了する</span>
              ) : (
                <span className="flex justify-center">
                  <IoMdPersonAdd size={16} />
                  <span className="ml-8">新規会員登録</span>
                </span>
              )
            }
            isPrimary={true}
            isLarge={false}
            onClick={() => {
              location.href = isStudentVerificationRequired
                ? PROFILE_AC_EMAIL
                : mailAuthLink;
            }}
          />
        </div>
        <CommonLink
          href={
            'https://itunes.apple.com/jp/app/%E3%83%88%E3%83%83%E3%83%97%E4%BC%81%E6%A5%AD%E3%81%B8%E5%86%85%E5%AE%9A%E3%82%92%E3%82%81%E3%81%96%E3%81%99%E5%B0%B1%E8%81%B7%E6%B4%BB%E5%8B%95%E3%82%A2%E3%83%97%E3%83%AA-%E5%A4%96%E8%B3%87%E5%B0%B1%E6%B4%BB%E3%83%89%E3%83%83%E3%83%88%E3%82%B3%E3%83%A0/id517232947?mt=8'
          }
        >
          <Image
            src="/next-image/common/logo/ios.png"
            height={40}
            width={135}
            alt="Download a Android App"
            className="h-[40px] block mt-12 ml-16"
          />
        </CommonLink>
        <CommonLink
          href={
            'https://play.google.com/store/apps/details?id=com.gaishishukatsu.Gaishishukatsu&hl=ja'
          }
        >
          <Image
            src="/next-image/common/logo/android.png"
            height={40}
            width={135}
            alt="Download a Android App"
            className="h-[40px] block mt-12 ml-16"
          />
        </CommonLink>
        {!isStudentVerificationRequired && (
          <CommonLink
            href={'/login'}
            className="flex text-link justify-center items-center mt-16 ml-16"
          >
            <MdExitToApp size={16} />
            <span>ログイン</span>
          </CommonLink>
        )}
      </div>
    </div>
  );
};
